import { getApiUrl } from "@lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface TabSubmission {
  id: number;
  title: string;
  artist: string;
  body: string;
  body_rendered?: string;
  user_id: number;
  user_email?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TabSubmissionListResponse {
  list: TabSubmission[];
}

export interface TabSubmissionPaginatedResponse {
  list: TabSubmission[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface TabSubmissionsQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface TabSubmissionUpdatePayload {
  title?: string;
  artist?: string;
  body?: string;
  body_rendered?: string;
}

function getAuthToken(): string | null {
  return localStorage.getItem("admin_token");
}

// API functions
async function fetchTabSubmissions(params?: TabSubmissionsQueryParams): Promise<TabSubmissionPaginatedResponse> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  
  const queryParams = new URLSearchParams();
  queryParams.append("token", token);
  if (params?.page) {
    queryParams.append("page", params.page.toString());
  }
  if (params?.page_size) {
    queryParams.append("page_size", params.page_size.toString());
  }
  if (params?.search) {
    queryParams.append("search", params.search);
  }
  if (params?.sort_by) {
    queryParams.append("sort_by", params.sort_by);
  }
  if (params?.sort_order) {
    queryParams.append("sort_order", params.sort_order);
  }
  
  const response = await fetch(`${apiUrl}/tab/submissions?${queryParams.toString()}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("Failed to fetch tab submissions");
  }
  return response.json();
}

async function fetchTabSubmission(id: number): Promise<TabSubmission> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/tab/submissions/${id}?token=${token}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch tab submission with ID ${id}`);
  }
  return response.json();
}

async function updateTabSubmissionApi({ id, payload }: { id: number; payload: TabSubmissionUpdatePayload }): Promise<TabSubmission> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/tab/submissions/${id}?token=${token}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to update tab submission" }));
    throw new Error(error.detail || "Failed to update tab submission");
  }
  return response.json();
}

async function deleteTabSubmissionApi(id: number): Promise<void> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/tab/submissions/${id}?token=${token}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to delete tab submission" }));
    throw new Error(error.detail || "Failed to delete tab submission");
  }
}

async function approveTabSubmissionApi(id: number, artistId: number): Promise<any> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/tab/submissions/${id}/approve?token=${token}&artist_id=${artistId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to approve tab submission" }));
    throw new Error(error.detail || "Failed to approve tab submission");
  }
  return response.json();
}

// TanStack Query hooks
export function useTabSubmissions(params?: TabSubmissionsQueryParams) {
  return useQuery<TabSubmissionPaginatedResponse, Error>({
    queryKey: ["tab-submissions", params],
    queryFn: () => fetchTabSubmissions(params),
  });
}

export function useTabSubmission(id: number) {
  return useQuery<TabSubmission, Error>({
    queryKey: ["tab-submission", id],
    queryFn: () => fetchTabSubmission(id),
    enabled: !!id,
  });
}

export function useUpdateTabSubmission() {
  const queryClient = useQueryClient();
  return useMutation<TabSubmission, Error, { id: number; payload: TabSubmissionUpdatePayload }>({
    mutationFn: updateTabSubmissionApi,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["tab-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["tab-submission", variables.id] });
    },
  });
}

export function useDeleteTabSubmission() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: deleteTabSubmissionApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tab-submissions"] });
    },
  });
}

export function useApproveTabSubmission() {
  const queryClient = useQueryClient();
  return useMutation<any, Error, { id: number; artistId: number }>({
    mutationFn: ({ id, artistId }) => approveTabSubmissionApi(id, artistId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tab-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["tabs"] });
    },
  });
}

